import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, Grid, Typography, makeStyles } from '@material-ui/core';
import { Input, Select } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';
import { MAX_COMMENT_LENGTH } from '../constants';

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const getActionOptions = (intl) => [
  {
    label: intl.formatMessage({ id: 'complaints.host.action.accept', defaultMessage: 'Accept' }),
    value: 'accept',
  },
  {
    label: intl.formatMessage({
      id: 'complaints.host.action.counterOffer',
      defaultMessage: 'Counter-offer',
    }),
    value: 'counter_offer',
  },
  {
    label: intl.formatMessage({ id: 'complaints.host.action.reject', defaultMessage: 'Reject' }),
    value: 'reject',
  },
];

const validate =
  ({ intl, maxCompensation }) =>
  (values) => {
    const errors = {};

    if (!values.action) {
      errors.action = intl.formatMessage({
        id: 'complaints.host.validation.actionRequired',
        defaultMessage: 'Please select an action',
      });
    }

    if (
      (values.action === 'reject' || values.action === 'counter_offer') &&
      !values.comment?.trim()
    ) {
      errors.comment = intl.formatMessage({
        id: 'complaints.host.validation.commentRequired',
        defaultMessage: 'Comment is required',
      });
    } else if (values.comment && values.comment.length > MAX_COMMENT_LENGTH) {
      errors.comment = intl.formatMessage(
        {
          id: 'complaints.host.validation.commentMax',
          defaultMessage: 'Comment must be at most {max} characters',
        },
        { max: MAX_COMMENT_LENGTH },
      );
    }

    const offeredCompensation = Number(values.offered_compensation);

    if (
      values.action === 'counter_offer' &&
      (!Number.isFinite(offeredCompensation) || offeredCompensation <= 0)
    ) {
      errors.offered_compensation = intl.formatMessage({
        id: 'complaints.host.validation.compensationRequired',
        defaultMessage: 'Please enter a compensation amount',
      });
    } else if (
      values.action === 'counter_offer' &&
      maxCompensation != null &&
      offeredCompensation > maxCompensation / 100
    ) {
      errors.offered_compensation = intl.formatMessage(
        {
          id: 'complaints.validation.compensationMax',
          defaultMessage: 'Cannot exceed booking price ({max} kr)',
        },
        { max: maxCompensation / 100 },
      );
    }

    return errors;
  };

const HostResponseForm = ({ onSubmit, isUpdating, conflict, maxCompensation }) => {
  const classes = useStyles();
  const intl = useIntl();

  const formik = useFormik({
    validate: validate({ intl, maxCompensation }),
    initialValues: {
      action: '',
      comment: '',
      offered_compensation: '',
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const data = { action: values.action };
      if (values.comment) data.comment = values.comment;
      if (values.action === 'counter_offer') {
        data.offered_compensation = Number(values.offered_compensation);
      }
      onSubmit(data);
    },
  });

  const { values, errors, handleSubmit, handleChange, handleBlur, isValid, isSubmitting } = formik;

  const showComment = values.action === 'reject' || values.action === 'counter_offer';
  const showCompensation = values.action === 'counter_offer';

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Select
            name="action"
            label={intl.formatMessage({ id: 'complaints.host.action', defaultMessage: 'Action' })}
            value={values.action}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            options={getActionOptions(intl)}
            required
          />
        </Grid>

        {showCompensation && (
          <Grid item xs={12} md={6}>
            <Input
              name="offered_compensation"
              label={intl.formatMessage({
                id: 'complaints.host.offeredCompensation',
                defaultMessage: 'Offered compensation (SEK)',
              })}
              type="number"
              value={values.offered_compensation}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              required
            />
          </Grid>
        )}

        {showComment && (
          <Grid item xs={12}>
            <Input
              name="comment"
              label={intl.formatMessage({
                id: 'complaints.host.comment',
                defaultMessage: 'Comment',
              })}
              value={values.comment}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              multiline
              minRows={3}
              maxRows={10}
              variant="outlined"
              required
            />
            <Box textAlign="right">
              <Typography
                variant="body2"
                color={values.comment?.length > MAX_COMMENT_LENGTH ? 'error' : 'initial'}
              >
                {values.comment?.length || 0}/{MAX_COMMENT_LENGTH}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <div className={classes.actions}>
        <Button
          color="primary"
          type="submit"
          variant="contained"
          disabled={isUpdating || isSubmitting || !isValid || conflict}
        >
          <FormattedMessage id="complaints.host.submit" defaultMessage="Submit response" />
        </Button>
      </div>
    </form>
  );
};

HostResponseForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
  conflict: PropTypes.bool,
  maxCompensation: PropTypes.number,
};

export default HostResponseForm;
